import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ArrowLeftRight, Plus, Trash2, Check, Ban, Eye, ArrowRight, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { UiSelect } from '../../components/UiSelect';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SkeletonTable, EmptyState, ButtonLoader } from '../../components/LoadingStates';
import { stockTransferService } from '../../services/inventory/stockTransfer';
import { inventoryItemService } from '../../services/inventory/inventoryItem';
import { organizationService } from '../../services/organization';
import { StockTransfer, TransferStatus, TransferLine, CreateTransferRequest, unitShort } from '../../types/inventory';
import { formatQty, formatDate } from './inventoryUtils';
import { Modal, Field, Th, inputCls } from './StockItemsTab';

interface Props { org: string; restaurantId: string; }

const STATUS_BADGE: Record<TransferStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-700',
};

export const TransfersTab: React.FC<Props> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewing, setViewing] = useState<StockTransfer | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; title: string; message: string; type: 'warning' | 'danger' | 'info'; confirmText: string; onConfirm: () => void }>({
    open: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: () => {},
  });

  const { data: transfers, isLoading } = useQuery(
    ['inventory-transfers', org, restaurantId],
    () => stockTransferService.getTransfers({ org, restaurantId }),
  );

  const completeMutation = useMutation(stockTransferService.completeTransfer, {
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-transfers']);
      queryClient.invalidateQueries(['inventory-items']);
      toast.success('Transfer completed — stock moved');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to complete transfer'); },
  });
  const cancelMutation = useMutation(stockTransferService.cancelTransfer, {
    onSuccess: () => { queryClient.invalidateQueries(['inventory-transfers']); toast.success('Transfer cancelled'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to cancel transfer'); },
  });

  const askComplete = (t: StockTransfer) => setConfirm({
    open: true, title: 'Complete Transfer', type: 'info', confirmText: 'Complete',
    message: `Move the stock from ${t.fromRestaurantName || 'source'} to ${t.toRestaurantName || 'destination'}? This deducts from the source and adds to the destination.`,
    onConfirm: () => { completeMutation.mutate(t.id); setConfirm(c => ({ ...c, open: false })); },
  });
  const askCancel = (t: StockTransfer) => setConfirm({
    open: true, title: 'Cancel Transfer', type: 'warning', confirmText: 'Cancel Transfer',
    message: 'Cancel this transfer? No stock will be moved.',
    onConfirm: () => { cancelMutation.mutate(t.id); setConfirm(c => ({ ...c, open: false })); },
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Move stock to and from other restaurants in your organization.</p>
        <button onClick={() => setIsFormOpen(true)} className="inline-flex items-center btn-primary">
          <Plus className="h-5 w-5 mr-2" /> New Transfer
        </button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={5} columns={6} />
      ) : !transfers || transfers.length === 0 ? (
        <EmptyState
          icon={<ArrowLeftRight className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No transfers yet"
          description="Create a transfer to send stock to another restaurant."
          action={<button onClick={() => setIsFormOpen(true)} className="inline-flex items-center btn-primary"><Plus className="h-5 w-5 mr-2" /> New Transfer</button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>Ref</Th><Th>Direction</Th><Th>From → To</Th><Th>Date</Th><Th className="text-right">Items</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {transfers.map(t => {
                const outgoing = t.fromRestaurantId === restaurantId;
                return (
                  <tr key={t.id}>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{t.transferNo || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', outgoing ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700')}>
                        {outgoing ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownLeft className="h-3 w-3" />}
                        {outgoing ? 'Outgoing' : 'Incoming'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>
                      <span className="flex items-center gap-1.5">
                        {t.fromRestaurantName || '—'} <ArrowRight className="h-3.5 w-3.5 text-gray-400" /> {t.toRestaurantName || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{formatDate(t.completedAt || t.createdAt)}</td>
                    <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.secondary }}>{t.items.length}</td>
                    <td className="px-4 py-3"><span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_BADGE[t.status])}>{t.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setViewing(t)} title="View" className="p-2 rounded-lg" style={{ color: currentTheme.colors.text.secondary, backgroundColor: currentTheme.colors.background.hover }}><Eye className="h-4 w-4" /></button>
                        {t.status === 'PENDING' && (
                          <>
                            <button onClick={() => askComplete(t)} title="Complete" className="p-2 rounded-lg" style={{ color: '#16a34a', backgroundColor: '#f0fdf4' }}><Check className="h-4 w-4" /></button>
                            <button onClick={() => askCancel(t)} title="Cancel" className="p-2 rounded-lg" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}><Ban className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isFormOpen && (
        <TransferForm org={org} restaurantId={restaurantId} onClose={() => setIsFormOpen(false)}
          onSaved={() => { setIsFormOpen(false); queryClient.invalidateQueries(['inventory-transfers']); }} />
      )}

      {viewing && <TransferDetails transfer={viewing} onClose={() => setViewing(null)} />}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title} message={confirm.message} confirmText={confirm.confirmText} type={confirm.type}
        isLoading={completeMutation.isLoading || cancelMutation.isLoading}
      />
    </div>
  );
};

interface DraftLine { inventoryItemId: string; quantity: string; }

const TransferForm: React.FC<{ org: string; restaurantId: string; onClose: () => void; onSaved: () => void }> = ({ org, restaurantId, onClose, onSaved }) => {
  const { currentTheme } = useTheme();
  const [toRestaurantId, setToRestaurantId] = useState('');
  const [notes, setNotes] = useState('');
  const [completeNow, setCompleteNow] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([{ inventoryItemId: '', quantity: '' }]);

  // Resolve the organization id from its code, then list its restaurants
  const { data: org_, } = useQuery(['organization', org], () => organizationService.getOrganizationByCode(org));
  const { data: restaurants } = useQuery(
    ['org-restaurants', org_?.id],
    () => organizationService.getRestaurantsByOrganization(org_!.id),
    { enabled: !!org_?.id },
  );
  const { data: items } = useQuery(['inventory-items', org, restaurantId], () => inventoryItemService.getItems({ org, restaurantId }));

  const destinations = useMemo(() => (restaurants ?? []).filter(r => r.id !== restaurantId && r.active), [restaurants, restaurantId]);

  const createMutation = useMutation(
    (payload: CreateTransferRequest) => stockTransferService.createTransfer(payload),
    {
      onSuccess: async (created) => {
        if (completeNow) {
          try { await stockTransferService.completeTransfer(created.id); } catch { /* surfaced by interceptor */ }
        }
        toast.success(completeNow ? 'Transfer completed — stock moved' : 'Transfer created');
        onSaved();
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to create transfer'); },
    },
  );

  const setLine = (i: number, patch: Partial<DraftLine>) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { inventoryItemId: '', quantity: '' }]);
  const removeLine = (i: number) => setLines(ls => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls);

  const validLines = lines.filter(l => l.inventoryItemId && Number(l.quantity) > 0);
  // Flag rows that would exceed available source stock
  const overflow = lines.some(l => {
    const item = items?.find(it => it.id === l.inventoryItemId);
    return item && Number(l.quantity) > item.currentStock;
  });
  const canSave = !!toRestaurantId && validLines.length > 0 && !overflow;

  const handleSave = () => {
    if (!toRestaurantId) { toast.error('Select a destination restaurant.'); return; }
    if (validLines.length === 0) { toast.error('Add at least one item with quantity.'); return; }
    if (overflow) { toast.error('A quantity exceeds available stock.'); return; }
    const payloadLines: TransferLine[] = validLines.map(l => ({ inventoryItemId: l.inventoryItemId, quantity: Number(l.quantity) }));
    createMutation.mutate({
      fromRestaurantId: restaurantId,
      toRestaurantId,
      items: payloadLines,
      notes: notes.trim() || undefined,
      organizationCode: org,
    });
  };

  return (
    <Modal title="New Stock Transfer" onClose={onClose} wide>
      <div className="space-y-4">
        <Field label="Transfer To (destination restaurant)" required>
          <UiSelect value={toRestaurantId} onChange={setToRestaurantId} placeholder="Select destination"
            options={[{ value: '', label: 'Select destination' }, ...destinations.map(r => ({ value: r.id, label: r.name }))]} />
          {destinations.length === 0 && restaurants && (
            <p className="mt-1 text-xs text-amber-600">No other restaurants available in this organization to transfer to.</p>
          )}
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>Items to transfer</label>
            <button type="button" onClick={addLine} className="text-sm font-medium flex items-center gap-1" style={{ color: currentTheme.colors.primary[600] }}><Plus className="h-4 w-4" /> Add row</button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => {
              const item = items?.find(it => it.id === l.inventoryItemId);
              const exceeds = item && Number(l.quantity) > item.currentStock;
              return (
                <div key={i}>
                  <div className="flex gap-2 items-center">
                    <UiSelect value={l.inventoryItemId} onChange={v => setLine(i, { inventoryItemId: v })} placeholder="Select item" className="flex-1"
                      options={[{ value: '', label: 'Select item' }, ...(items || []).filter(it => it.active).map(it => ({ value: it.id, label: `${it.name} (${formatQty(it.currentStock)} ${unitShort(it.unit)})` }))]} />
                    <div className="relative w-32">
                      <input type="number" step="any" min="0" value={l.quantity} onChange={e => setLine(i, { quantity: e.target.value })}
                        className={clsx(inputCls, exceeds && 'border-red-400')} placeholder="Qty" />
                      {item && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{unitShort(item.unit)}</span>}
                    </div>
                    <button type="button" onClick={() => removeLine(i)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {exceeds && <p className="text-xs text-red-600 mt-1">Only {formatQty(item!.currentStock)} {unitShort(item!.unit)} available</p>}
                </div>
              );
            })}
          </div>
        </div>

        <Field label="Notes"><input value={notes} onChange={e => setNotes(e.target.value)} className={inputCls} placeholder="optional" /></Field>

        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: currentTheme.colors.text.primary }}>
          <input type="checkbox" checked={completeNow} onChange={e => setCompleteNow(e.target.checked)} className="h-4 w-4 rounded" />
          Complete immediately (move stock now)
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button type="button" onClick={handleSave} disabled={!canSave || createMutation.isLoading}
            className="px-4 py-2 btn-primary">
            {createMutation.isLoading ? <ButtonLoader text="Saving..." /> : (completeNow ? 'Save & Complete' : 'Create Transfer')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const TransferDetails: React.FC<{ transfer: StockTransfer; onClose: () => void }> = ({ transfer, onClose }) => {
  const { currentTheme } = useTheme();
  return (
    <Modal title={`Transfer ${transfer.transferNo || ''}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center justify-center gap-3 text-sm font-medium p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.hover, color: currentTheme.colors.text.primary }}>
          {transfer.fromRestaurantName} <ArrowRight className="h-4 w-4 text-gray-400" /> {transfer.toRestaurantName}
        </div>
        <div className="space-y-1">
          {transfer.items.map((l, i) => (
            <div key={i} className="flex justify-between text-sm py-1.5 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
              <span style={{ color: currentTheme.colors.text.primary }}>{l.itemName || l.inventoryItemId}</span>
              <span style={{ color: currentTheme.colors.text.secondary }}>{formatQty(l.quantity)} {unitShort(l.unit)}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm">
          <span style={{ color: currentTheme.colors.text.secondary }}>Status</span>
          <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium', STATUS_BADGE[transfer.status])}>{transfer.status}</span>
        </div>
        {transfer.notes && <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Note: {transfer.notes}</p>}
      </div>
    </Modal>
  );
};
